import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const ORG_ROLES = ['MEMBER', 'ORG_ADMIN', 'AUDITOR', 'VIEWER'];

function PctBar({ value, color = '#6366f1' }) {
  return (
    <div style={{ background: '#e5e7eb', borderRadius: 4, height: 7, overflow: 'hidden', flex: 1, minWidth: 60 }}>
      <div style={{ width: `${Math.min(100, value || 0)}%`, background: color, height: '100%', transition: 'width .4s' }} />
    </div>
  );
}

function StatCard({ icon, label, value, color = '#6366f1' }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', flex: '1 1 140px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: color + '1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={`fas ${icon}`} style={{ color, fontSize: 16 }} />
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>{value ?? '—'}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{label}</div>
        </div>
      </div>
    </div>
  );
}

function AddUserModal({ orgId, onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', login: '', password: '', orgRole: 'MEMBER' });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.login || !form.email) { setErr(t('ClientView.loginEmailRequired')); return; }
    setSaving(true);
    setErr('');
    try {
      const res = await axiosInstance.post(`/partner/context/clients/${orgId}/users`, form, { headers: authHeaders() });
      onCreated(res.data);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || t('ClientView.userCreationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const inp = (k, ph, type = 'text') => (
    <input type={type} value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph}
      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }} />
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 420, boxShadow: '0 24px 48px rgba(0,0,0,.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{t('ClientView.addUser')}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }}>×</button>
        </div>
        {err && <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('ClientView.firstName')}</div>{inp('firstName', t('ClientView.firstNamePlaceholder'))}</div>
          <div><div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('ClientView.lastName')}</div>{inp('lastName', t('ClientView.lastNamePlaceholder'))}</div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('ClientView.email')}</div>
        {inp('email', t('ClientView.emailPlaceholder'), 'email')}
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('ClientView.login')}</div>
        {inp('login', t('ClientView.loginPlaceholder'))}
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('ClientView.temporaryPassword')}</div>
        {inp('password', t('ClientView.passwordPlaceholder'), 'password')}
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('ClientView.role')}</div>
        <select value={form.orgRole} onChange={e => set('orgRole', e.target.value)}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 16 }}>
          {ORG_ROLES.map(r => <option key={r}>{r}</option>)}
        </select>
        <button onClick={submit} disabled={saving}
          style={{ width: '100%', padding: 10, background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          {saving ? t('ClientView.creating') : t('ClientView.createUser')}
        </button>
      </div>
    </div>
  );
}

// ── Tab: Overview ─────────────────────────────────────────────────────────────
function OverviewTab({ summary }) {
  const { t } = useTranslation();
  if (!summary) return <div style={{ color: '#9ca3af', padding: 20 }}>{t('ClientView.loading')}</div>;
  const riskColor = summary.riskScore >= 75 ? '#16a34a' : summary.riskScore >= 50 ? '#d97706' : '#dc2626';
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <StatCard icon="fa-sitemap"        label={t('ClientView.frameworks')}  value={summary.frameworkCount} color="#6366f1" />
        <StatCard icon="fa-users"          label={t('ClientView.users')}       value={summary.userCount}      color="#0891b2" />
        <StatCard icon="fa-clipboard-check"label={t('ClientView.auditItems')} value={summary.auditItems}     color="#16a34a" />
        <StatCard icon="fa-shield-alt"     label={t('ClientView.riskScore')}  value={summary.riskScore}      color={riskColor} />
      </div>
      {summary.frameworkCount > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>{t('ClientView.enrolledFrameworks')}</div>
          {(summary.frameworks || []).map(fw => (
            <div key={fw.id} style={{ fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
              <i className="fas fa-check-circle" style={{ color: '#16a34a', marginRight: 8 }} />{fw.name}
            </div>
          ))}
        </div>
      )}
      {summary.auditItems > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginTop: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>{t('ClientView.complianceSummary')}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <span style={{ width: 90, fontSize: 12, color: '#6b7280' }}>{t('ClientView.compliant')}</span>
            <PctBar value={(summary.compliantCount / summary.auditItems) * 100} color="#16a34a" />
            <span style={{ fontSize: 12, width: 40 }}>{summary.compliantCount}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ width: 90, fontSize: 12, color: '#6b7280' }}>{t('ClientView.failing')}</span>
            <PctBar value={(summary.failingCount / summary.auditItems) * 100} color="#dc2626" />
            <span style={{ fontSize: 12, width: 40 }}>{summary.failingCount}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab: Frameworks ───────────────────────────────────────────────────────────
function FrameworksTab({ orgId }) {
  const { t } = useTranslation();
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [enrolling, setEnrolling]   = useState(null);
  const [msg, setMsg]               = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [clientFwRes] = await Promise.all([
        axiosInstance.get(`/partner/context/clients/${orgId}/frameworks`, { headers: authHeaders() }),
      ]);
      setFrameworks(clientFwRes.data || []);
    } catch { setMsg(t('ClientView.failedToLoadFrameworks')); }
    finally { setLoading(false); }
  }, [orgId, t]);

  useEffect(() => { load(); }, [load]);

  const signup = async (frameworkId) => {
    setEnrolling(frameworkId);
    setMsg('');
    try {
      await axiosInstance.post(`/partner/context/clients/${orgId}/frameworks/${frameworkId}/signup`, {}, { headers: authHeaders() });
      setMsg(t('ClientView.frameworkEnrolledSuccess'));
      load();
    } catch (e) {
      setMsg(e.response?.data?.message || t('ClientView.signupFailed'));
    } finally {
      setEnrolling(null);
    }
  };

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;

  return (
    <div>
      {msg && <div style={{ padding: '8px 12px', background: '#dbeafe', color: '#1e40af', borderRadius: 6, fontSize: 13, marginBottom: 14 }}>{msg}</div>}
      {frameworks.map(fw => (
        <div key={fw.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: fw.enrolled ? 10 : 0 }}>
            <div>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{fw.displayName}</span>
              {fw.enrolled && (
                <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 8, background: '#dcfce7', color: '#166534' }}>{t('ClientView.enrolled')}</span>
              )}
            </div>
            {!fw.enrolled && (
              <button onClick={() => signup(fw.id)} disabled={enrolling === fw.id}
                style={{ padding: '6px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                {enrolling === fw.id ? <i className="fas fa-spinner fa-spin" /> : t('ClientView.enrolClient')}
              </button>
            )}
          </div>
          {fw.enrolled && fw.totalControls > 0 && (
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: '#6b7280', width: 80 }}>{t('ClientView.compliant')}</span>
                <PctBar value={fw.compliantPct} color="#16a34a" />
                <span style={{ fontSize: 11, width: 36 }}>{fw.compliantPct}%</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#6b7280', width: 80 }}>{t('ClientView.failing')}</span>
                <PctBar value={(fw.failingCount / fw.totalControls) * 100} color="#dc2626" />
                <span style={{ fontSize: 11, width: 36 }}>{fw.failingCount}</span>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Tab: Audits ───────────────────────────────────────────────────────────────
function AuditsTab({ orgId }) {
  const { t } = useTranslation();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(`/partner/context/clients/${orgId}/audits`, { headers: authHeaders() });
        setAudits(res.data || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [orgId]);

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;
  if (audits.length === 0) return <div style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>{t('ClientView.noAuditsYet')}</div>;

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            {[t('ClientView.auditId'), t('ClientView.created'), t('ClientView.controls'), t('ClientView.compliant'), t('ClientView.failing'), t('ClientView.progress')].map(h => (
              <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {audits.map(a => (
            <tr key={a.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '10px 14px', fontWeight: 500 }}>{a.auditId}</td>
              <td style={{ padding: '10px 14px', color: '#6b7280' }}>{a.createdDate?.slice(0, 10)}</td>
              <td style={{ padding: '10px 14px' }}>{a.totalControls}</td>
              <td style={{ padding: '10px 14px' }}>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>{a.compliantCount}</span>
              </td>
              <td style={{ padding: '10px 14px' }}>
                <span style={{ color: '#dc2626', fontWeight: 600 }}>{a.failingCount}</span>
              </td>
              <td style={{ padding: '10px 14px', minWidth: 120 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <PctBar value={a.pct} color={a.pct >= 75 ? '#16a34a' : a.pct >= 40 ? '#d97706' : '#dc2626'} />
                  <span style={{ fontSize: 11, flexShrink: 0 }}>{a.pct}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Tab: Users ────────────────────────────────────────────────────────────────
function UsersTab({ orgId }) {
  const { t } = useTranslation();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/partner/context/clients/${orgId}/users`, { headers: authHeaders() });
      setUsers(res.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const ROLE_COLORS = { ORG_ADMIN: { bg: '#f5f3ff', color: '#6b21a8' }, MEMBER: { bg: '#f3f4f6', color: '#374151' },
                        AUDITOR: { bg: '#dbeafe', color: '#1e40af' }, VIEWER: { bg: '#fef9c3', color: '#854d0e' } };

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button onClick={() => setShowAdd(true)}
          style={{ padding: '7px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
          <i className="fas fa-user-plus" style={{ marginRight: 6 }} /> {t('ClientView.addUser')}
        </button>
      </div>
      {users.length === 0 ? (
        <div style={{ color: '#9ca3af', textAlign: 'center', padding: 30 }}>{t('ClientView.noUsersYet')}</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[t('ClientView.name'), t('ClientView.login'), t('ClientView.email'), t('ClientView.role'), t('ClientView.status')].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const rb = ROLE_COLORS[u.orgRole] || ROLE_COLORS.MEMBER;
                return (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 500 }}>{u.firstName} {u.lastName}</td>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>{u.login}</td>
                    <td style={{ padding: '10px 14px', color: '#6b7280' }}>{u.email}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, ...rb }}>{u.orgRole}</span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8,
                        background: u.active ? '#dcfce7' : '#fee2e2', color: u.active ? '#166534' : '#991b1b' }}>
                        {u.active ? t('ClientView.active') : t('ClientView.inactive')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {showAdd && (
        <AddUserModal orgId={orgId} onClose={() => setShowAdd(false)}
          onCreated={u => { setUsers(p => [...p, u]); }} />
      )}
    </div>
  );
}

// ── Dashboard summary panel — shown above the tabs ─────────────────────────
function DashboardSummary({ orgId, orgName, onOpenWorkspace }) {
  const { t } = useTranslation();
  const [dashboard,   setDashboard]   = useState(null);
  const [readiness,   setReadiness]   = useState(null);
  const [frameworks,  setFrameworks]  = useState([]);
  const [connections, setConnections] = useState([]);
  const [audits,      setAudits]      = useState([]);
  const [assetCount,  setAssetCount]  = useState(0);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get(`/partner/context/clients/${orgId}/dashboard`,         { headers: authHeaders() }),
      axiosInstance.get(`/partner/context/clients/${orgId}/readiness-score`,   { headers: authHeaders() }),
      axiosInstance.get(`/partner/cpa/orgs/${orgId}/frameworks`,               { headers: authHeaders() }),
      axiosInstance.get(`/partner/context/clients/${orgId}/vendors/connections`,{ headers: authHeaders() }),
      axiosInstance.get(`/partner/context/clients/${orgId}/audits`,            { headers: authHeaders() }),
      axiosInstance.get(`/partner/context/clients/${orgId}/assets?page=0&size=1`,{ headers: authHeaders() }),
    ]).then(([dRes, rRes, fwRes, cnRes, auRes, asRes]) => {
      if (dRes.status === 'fulfilled')  setDashboard(dRes.value.data);
      if (rRes.status === 'fulfilled')  setReadiness(rRes.value.data);
      if (fwRes.status === 'fulfilled') setFrameworks(fwRes.value.data || []);
      if (cnRes.status === 'fulfilled') setConnections(cnRes.value.data || []);
      if (auRes.status === 'fulfilled') setAudits(auRes.value.data || []);
      if (asRes.status === 'fulfilled') setAssetCount(asRes.value.data?.[0]?.totalCount || 0);
    }).finally(() => setLoading(false));
  }, [orgId]);

  const score      = readiness?.score ?? 0;
  const scoreLabel = readiness?.label || '—';
  const scoreColor = score >= 80 ? '#10b981' : score >= 60 ? '#3b82f6' : score >= 40 ? '#f59e0b' : '#ef4444';
  const compliance = dashboard?.complianceData || {};

  const QUICK_ACTIONS = [
    { tab: 'asm',          icon: 'fas fa-globe',           label: t('ClientView.externalAttackSurface'), color: '#3b82f6' },
    { tab: 'iasm',         icon: 'fas fa-network-wired',   label: t('ClientView.internalAttackSurface'), color: '#8b5cf6' },
    { tab: 'audits',       icon: 'fas fa-clipboard-check', label: t('ClientView.audits'),                  color: '#10b981' },
    { tab: 'frameworks',   icon: 'fas fa-layer-group',     label: t('ClientView.frameworks'),              color: '#f59e0b' },
    { tab: 'integrations', icon: 'fas fa-plug',            label: t('ClientView.vendorsAndAssets'),        color: '#ef4444' },
    { tab: 'policies',     icon: 'fas fa-file-shield',     label: t('ClientView.policies'),                color: '#6366f1' },
    { tab: 'documents',    icon: 'fas fa-file-alt',        label: t('ClientView.documents'),               color: '#0ea5e9' },
    { tab: 'team',         icon: 'fas fa-users',           label: t('ClientView.team'),                    color: '#ec4899' },
  ];

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} /></div>;

  return (
    <>
      {/* Posture hero + KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
        <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', color: '#fff',
          borderRadius: 14, padding: '20px 22px', display: 'flex', gap: 18, alignItems: 'center' }}>
          <div style={{ position: 'relative', width: 92, height: 92 }}>
            <svg viewBox="0 0 100 100" style={{ width: 92, height: 92, transform: 'rotate(-90deg)' }}>
              <circle cx={50} cy={50} r={42} stroke="rgba(255,255,255,0.15)" strokeWidth={10} fill="none" />
              <circle cx={50} cy={50} r={42} stroke={scoreColor} strokeWidth={10} fill="none"
                strokeDasharray={`${(score / 100) * 264} 264`} strokeLinecap="round" />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexDirection: 'column' }}>
              <span style={{ fontSize: 22, fontWeight: 800 }}>{score}</span>
              <span style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>{t('ClientView.posture')}</span>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>{t('ClientView.readiness')}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, marginTop: 4 }}>{scoreLabel}</div>
            <div style={{ fontSize: '0.83rem', color: '#cbd5e1', marginTop: 6 }}>
              {t('ClientView.testsPassing', { passed: (dashboard?.totalPassedTests ?? 0), total: (dashboard?.totalTests ?? 0) })}
              {(dashboard?.totalFailedTests ?? 0) > 0 && <span style={{ color: '#fecaca' }}>{t('ClientView.testsFailingSuffix', { count: dashboard.totalFailedTests })}</span>}
            </div>
            {/* Composite-score breakdown: tests ×0.8 + policies ×0.1 + docs ×0.1 */}
            <div style={{ marginTop: 10, display: 'flex', gap: 10, fontSize: 11 }}>
              {[
                { k: t('ClientView.tests'),     pct: readiness?.testScore ?? 0,     w: '80%' },
                { k: t('ClientView.policies'),  pct: readiness?.policyScore ?? 0,   w: '10%' },
                { k: t('ClientView.documents'), pct: readiness?.evidenceScore ?? 0, w: '10%' },
              ].map(b => (
                <div key={b.k} style={{ flex: 1, background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '6px 8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1', fontWeight: 600 }}>
                    <span>{b.k}</span><span style={{ opacity: 0.7 }}>{b.w}</span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginTop: 2 }}>{b.pct}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {[
            { label: t('ClientView.frameworks'),  value: frameworks.length,  icon: 'fa-layer-group', color: '#f59e0b' },
            { label: t('ClientView.vendors'),     value: connections.length, icon: 'fa-plug',        color: '#ef4444' },
            { label: t('ClientView.assets'),      value: assetCount,         icon: 'fa-cubes',       color: '#3b82f6' },
            { label: t('ClientView.audits'),      value: audits.length,      icon: 'fa-clipboard-check', color: '#10b981' },
          ].map(k => (
            <div key={k.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
              padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: k.color + '18',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className={`fas ${k.icon}`} style={{ color: k.color, fontSize: 16 }} />
              </div>
              <div>
                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{k.value}</div>
                <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 2 }}>{k.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Framework readiness bars */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 12 }}>
          <i className="fas fa-shield-halved" style={{ marginRight: 8, color: '#3b82f6' }} />
          {t('ClientView.frameworkReadiness')}
        </div>
        {Object.keys(compliance).length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '0.85rem', padding: 10, textAlign: 'center' }}>
            {t('ClientView.noFrameworkData')}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {Object.entries(compliance).map(([name, pct]) => {
              const rounded = Math.round(pct);
              const color = rounded >= 80 ? '#10b981' : rounded >= 50 ? '#f59e0b' : '#ef4444';
              return (
                <div key={name} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{name}</span>
                    <span style={{ fontWeight: 800, color }}>{rounded}%</span>
                  </div>
                  <div style={{ background: '#e2e8f0', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ background: color, height: '100%', width: `${rounded}%`, transition: 'width 0.4s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick-jump actions: open the deep workspace modal on the right tab */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 12 }}>
          <i className="fas fa-bolt" style={{ marginRight: 8, color: '#f59e0b' }} />
          {t('ClientView.manageOrg', { name: orgName })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
          {QUICK_ACTIONS.map(a => (
            <button key={a.tab} onClick={() => onOpenWorkspace(a.tab)}
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                padding: '14px 12px', cursor: 'pointer', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = a.color; e.currentTarget.style.background = a.color + '08'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#f8fafc'; }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: a.color + '18',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className={a.icon} style={{ color: a.color, fontSize: 15 }} />
              </div>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>{a.label}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Root component ─────────────────────────────────────────────────────────────
export default function ClientView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clients, setClients]       = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsFailed, setClientsFailed]   = useState(false);
  const [selectedId, setSelectedId] = useState(
    localStorage.getItem('partnerContextOrgId') ? Number(localStorage.getItem('partnerContextOrgId')) : null);
  const [summary, setSummary]       = useState(null);
  const [tab, setTab]               = useState('overview');
  const [workspaceTab, setWorkspaceTab] = useState(null);
  const [Workspace, setWorkspace]   = useState(null);

  // Load all clients in the CPA's portfolio so we can offer a dropdown
  useEffect(() => {
    axiosInstance.get('/partner/context/clients', { headers: authHeaders() })
      .then(r => {
        const list = r.data || [];
        setClients(list);
        if (!selectedId && list.length > 0) {
          setSelectedId(list[0].id);
          localStorage.setItem('partnerContextOrgId',   String(list[0].id));
          localStorage.setItem('partnerContextOrgName', list[0].name);
        }
      })
      .catch(() => setClientsFailed(true))
      .finally(() => setClientsLoading(false));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch the per-client summary whenever selection changes
  useEffect(() => {
    if (!selectedId) return;
    const found = clients.find(c => c.id === selectedId);
    if (found) {
      localStorage.setItem('partnerContextOrgId',   String(selectedId));
      localStorage.setItem('partnerContextOrgName', found.name);
    }
    axiosInstance.get(`/partner/context/clients/${selectedId}/summary`, { headers: authHeaders() })
      .then(r => setSummary(r.data))
      .catch(() => setSummary(null));
  }, [selectedId, clients]);

  // Lazy-load the full CpaClientWorkspace modal so it doesn't bloat the initial bundle
  useEffect(() => {
    import('../Marketplace/CpaClientWorkspace').then(mod => setWorkspace(() => mod.default));
  }, []);

  const selectedOrg = clients.find(c => c.id === selectedId);

  if (clientsLoading) {
    return (
      <div style={{ padding: 40, fontFamily: 'Inter, sans-serif', maxWidth: 800 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 40, textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 28, color: '#94a3b8', display: 'block', marginBottom: 12 }} />
          <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{t('ClientView.loadingClients', 'Loading client organisations…')}</div>
        </div>
      </div>
    );
  }

  if (clientsFailed && clients.length === 0) {
    return (
      <div style={{ padding: 40, fontFamily: 'Inter, sans-serif', maxWidth: 800 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 40, textAlign: 'center' }}>
          <i className="fas fa-triangle-exclamation" style={{ fontSize: 36, color: '#f59e0b', display: 'block', marginBottom: 12 }} />
          <div style={{ fontWeight: 700, color: '#374151', marginBottom: 6 }}>{t('ClientView.loadClientsFailed', 'Could not load your client organisations')}</div>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{t('ClientView.loadClientsFailedHint', 'Refresh the page to try again.')}</div>
        </div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div style={{ padding: 40, fontFamily: 'Inter, sans-serif', maxWidth: 800 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 40, textAlign: 'center' }}>
          <i className="fas fa-building" style={{ fontSize: 36, color: '#cbd5e1', display: 'block', marginBottom: 12 }} />
          <div style={{ fontWeight: 700, color: '#374151', marginBottom: 6 }}>{t('ClientView.noClientOrgsYet')}</div>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{t('ClientView.addFirstClientPrefix')}<a href="#/dashboard/clients" style={{ color: '#6366f1', fontWeight: 600 }}>{t('ClientView.myClients')}</a>{t('ClientView.addFirstClientSuffix')}</div>
        </div>
      </div>
    );
  }

  const s = {
    page: { padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 1100 },
    tabs: { display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 20 },
    tab:  (a) => ({ padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                    fontWeight: 600, fontSize: 13, color: a ? '#6366f1' : '#6b7280',
                    borderBottom: a ? '2px solid #6366f1' : '2px solid transparent', marginBottom: -2 }),
  };

  return (
    <div style={s.page}>
      {/* Header with org dropdown + back-to-list */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22,
        background: '#fff', padding: '14px 18px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <button onClick={() => navigate('/dashboard/clients')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 13, fontWeight: 600, padding: 0 }}>
          <i className="fas fa-arrow-left" style={{ marginRight: 6 }} /> {t('ClientView.myClients')}
        </button>
        <span style={{ color: '#d1d5db' }}>›</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('ClientView.clientOrganisation')}
          </div>
          <select value={selectedId || ''} onChange={e => setSelectedId(Number(e.target.value))}
            style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6,
              fontSize: '1rem', fontWeight: 700, color: '#111827', marginTop: 2,
              minWidth: 280, background: '#fff' }}>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 10, background: '#f5f3ff', color: '#6b21a8', fontWeight: 600 }}>
          <i className="fas fa-eye" style={{ marginRight: 4 }} />{t('ClientView.clientView')}
        </span>
      </div>

      {/* Dashboard summary: posture, KPIs, framework readiness, quick-jump cards */}
      {selectedId && (
        <DashboardSummary
          orgId={selectedId}
          orgName={selectedOrg?.name || ''}
          onOpenWorkspace={(t) => setWorkspaceTab(t)}
        />
      )}

      {/* Inline drill-down tabs — kept from the previous design for fast scanning */}
      <div style={s.tabs}>
        {[['overview','fa-th-large',t('ClientView.overview')], ['frameworks','fa-sitemap',t('ClientView.frameworks')],
          ['audits','fa-clipboard-check',t('ClientView.audits')], ['users','fa-users',t('ClientView.users')]].map(([key, icon, label]) => (
          <button key={key} style={s.tab(tab === key)} onClick={() => setTab(key)}>
            <i className={`fas ${icon}`} style={{ marginRight: 6 }} />{label}
          </button>
        ))}
      </div>

      {tab === 'overview'   && <OverviewTab   summary={summary} />}
      {tab === 'frameworks' && <FrameworksTab orgId={selectedId} />}
      {tab === 'audits'     && <AuditsTab     orgId={selectedId} />}
      {tab === 'users'      && <UsersTab      orgId={selectedId} />}

      {/* Full workspace modal — same component used from the Marketplace dashboard */}
      {workspaceTab && Workspace && selectedOrg && (
        <Workspace
          org={{ id: selectedOrg.id, name: selectedOrg.name }}
          initialTab={workspaceTab}
          onClose={() => setWorkspaceTab(null)}
        />
      )}
    </div>
  );
}
